const API_BASE_URL = '/api';

export interface OpenCallSubmissionPayload {
  fullLegalName: string;
  artistName: string;
  gender?: string;
  dateOfBirth: string;
  nationality: string;
  countryOfResidence: string;
  city: string;
  email: string;
  phoneNumber: string;
  artistCategories: string[];
  otherCategory?: string;
  socialProfile: string;
  website?: string;
  portfolioProfile?: string;
  videoProfile?: string;
  primaryPlatform: string;
  shortBio: string;
  yearsOfExperience: string;
  artStyle: string;
  themes: string;
  fullTimeArtist: 'yes' | 'no';
  hasExhibited: 'yes' | 'no';
  exhibitionDetails?: string;
  hasSoldWork: 'yes' | 'no';
  acceptsCommissions: 'yes' | 'no';
  displayConsent: boolean;
  accuracyConsent: boolean;
  typedSignature: string;
  agreementDate: string;
}

export interface OpenCallSubmissionRecord {
  id: string;
  fullLegalName: string;
  artistName: string;
  gender: string | null;
  dateOfBirth: string;
  nationality: string;
  countryOfResidence: string;
  city: string;
  email: string;
  phoneNumber: string;
  artistCategories: string[];
  otherCategory: string | null;
  socialProfile: string;
  website: string | null;
  portfolioProfile: string | null;
  videoProfile: string | null;
  primaryPlatform: string;
  shortBio: string;
  yearsOfExperience: string;
  artStyle: string;
  themes: string;
  fullTimeArtist: boolean;
  hasExhibited: boolean;
  exhibitionDetails: string | null;
  hasSoldWork: boolean;
  acceptsCommissions: boolean;
  displayConsent: boolean;
  accuracyConsent: boolean;
  typedSignature: string;
  agreementDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface OpenCallAdminFilters {
  sortBy?: 'createdAt' | 'fullLegalName' | 'artistName' | 'email' | 'countryOfResidence';
  sortDir?: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
}

export interface Product {
  _id: string;
  title: string;
  description: string;
  price: number;
  cover_image_url: string;
  cover_image_base?: string;
  active: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
  // File fields are admin-only — the public store routes strip them so the
  // paid file can't be fetched without checking out.
  file_key?: string;
  file_url?: string;
  file_name?: string;
  file_content_type?: string;
}

export type PaymentMode = 'live' | 'test' | 'missing';

export interface ProductUploadTarget {
  key: string;
  url: string;
  contentType: string;
}

export interface CreateOrderPayload {
  product_id: string;
  buyer_name: string;
  buyer_email: string;
}

export interface CreateOrderResponse {
  order_id: string;
  reference: string;
  amount: number;
  currency: string;
}

export type OrderStatus = 'pending' | 'paid' | 'failed' | 'abandoned';

export interface VerifyOrderResponse {
  status: OrderStatus;
  download_url?: string;
  message?: string;
}

export interface OrderRecord {
  _id: string;
  product_id: string;
  product_title: string;
  product_file_url: string;
  product_file_name: string;
  buyer_name: string;
  buyer_email: string;
  amount: number;
  currency: string;
  reference: string;
  status: OrderStatus;
  paystack_transaction_id: string | null;
  fulfilled_at: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrdersAdminFilters {
  sortBy?: 'createdAt' | 'buyer_name' | 'amount' | 'status';
  sortDir?: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
  status?: OrderStatus;
}

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export const api = {
  // --- AUTH ---
  login: async (email: string, password: string) => {
    const res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.message || 'Login failed');
    }
    const data = await res.json();
    localStorage.setItem('token', data.token);
    return data;
  },

  resetPassword: async (currentPassword: string, newPassword: string) => {
    const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ currentPassword, newPassword })
    });
    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.message || 'Password reset failed');
    }
    return res.json();
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // --- POSTS ---
  // Public
  getPosts: async () => {
    const res = await fetch(`${API_BASE_URL}/posts`);
    if (!res.ok) throw new Error('Failed to fetch posts');
    return res.json();
  },

  getPostBySlug: async (slug: string) => {
    const res = await fetch(`${API_BASE_URL}/posts/${slug}`);
    if (!res.ok) throw new Error('Failed to fetch post');
    return res.json();
  },

  // Admin
  getAdminPosts: async () => {
    const res = await fetch(`${API_BASE_URL}/posts/admin`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch posts');
    return res.json();
  },

  getPostById: async (id: string) => {
    const res = await fetch(`${API_BASE_URL}/posts/id/${id}`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch post');
    return res.json();
  },

  createPost: async (postData: any) => {
    const res = await fetch(`${API_BASE_URL}/posts`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(postData)
    });
    if (!res.ok) {
       const errorData = await res.json();
       throw new Error(errorData.message || 'Failed to create post');
    }
    return res.json();
  },

  updatePost: async (id: string, postData: any) => {
    const res = await fetch(`${API_BASE_URL}/posts/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(postData)
    });
    if (!res.ok) {
       const errorData = await res.json();
       throw new Error(errorData.message || 'Failed to update post');
    }
    return res.json();
  },

  deletePost: async (id: string) => {
    const res = await fetch(`${API_BASE_URL}/posts/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete post');
    return res.json();
  },

  uploadImage: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    });
    if (!res.ok) throw new Error('Failed to upload image');
    const data = await res.json();
    return data.url;
  },

  // --- ARTWORK ---
  getArtworks: async () => {
    const res = await fetch(`${API_BASE_URL}/artwork`);
    if (!res.ok) throw new Error('Failed to fetch artworks');
    return res.json();
  },

  getArtwork: async (id: string) => {
    const res = await fetch(`${API_BASE_URL}/artwork/${encodeURIComponent(id)}`);
    if (!res.ok) throw new Error('Failed to fetch artwork');
    return res.json();
  },

  getArtworksByIds: async (ids: string[]) => {
    if (!ids.length) return [];
    const res = await fetch(`${API_BASE_URL}/artwork?ids=${ids.join(',')}`);
    if (!res.ok) throw new Error('Failed to fetch artworks');
    return res.json();
  },

  getArtworksByArtist: async (artistId: string) => {
    const res = await fetch(`${API_BASE_URL}/artwork?artist_id=${artistId}`);
    if (!res.ok) throw new Error('Failed to fetch artworks by artist');
    return res.json();
  },

  getPostsByArtist: async (artistId: string) => {
    const res = await fetch(`${API_BASE_URL}/posts?artist_id=${artistId}`);
    if (!res.ok) throw new Error('Failed to fetch posts by artist');
    return res.json();
  },

  getFeaturedArtworks: async () => {
    const res = await fetch(`${API_BASE_URL}/artwork/featured`);
    if (!res.ok) throw new Error('Failed to fetch featured artworks');
    return res.json();
  },

  createArtwork: async (formData: FormData) => {
    const res = await fetch(`${API_BASE_URL}/artwork`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to create artwork');
    }
    return res.json();
  },

  updateArtwork: async (id: string, formData: FormData) => {
    const res = await fetch(`${API_BASE_URL}/artwork/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to update artwork');
    }
    return res.json();
  },

  deleteArtwork: async (id: string) => {
    const res = await fetch(`${API_BASE_URL}/artwork/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete artwork');
    return res.json();
  },

  // --- SITE CONTENT ---
  getSiteContent: async (page: string) => {
    const res = await fetch(`${API_BASE_URL}/site-content/${page}`);
    if (!res.ok) throw new Error(`Failed to fetch content for page: ${page}`);
    return res.json();
  },

  updateSiteContent: async (page: string, content: Record<string, any>) => {
    const res = await fetch(`${API_BASE_URL}/site-content/${page}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(content)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to update page content');
    }
    return res.json();
  },

  // --- CONTACT ---
  submitContactForm: async (data: { name: string; email: string; subject: string; message: string }) => {
    const res = await fetch(`${API_BASE_URL}/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to submit form');
    }
    return res.json();
  },

  submitOpenCallForm: async (data: OpenCallSubmissionPayload) => {
    const res = await fetch(`${API_BASE_URL}/open-call`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to submit open call form');
    }
    return res.json();
  },

  getAdminOpenCallSubmissions: async (filters: OpenCallAdminFilters = {}): Promise<OpenCallSubmissionRecord[]> => {
    const params = new URLSearchParams();
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortDir) params.set('sortDir', filters.sortDir);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);

    const queryString = params.toString();
    const res = await fetch(`${API_BASE_URL}/open-call/admin${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to fetch Open Call submissions');
    }
    return res.json();
  },

  runOpenCallMigrationDeploy: async (): Promise<{ message: string; output: string }> => {
    const res = await fetch(`${API_BASE_URL}/open-call/admin/migrate`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.output || data.message || 'Failed to run Prisma migration deploy');
    }
    return data;
  },

  // --- ARTISTS ---
  getArtists: async () => {
    const res = await fetch(`${API_BASE_URL}/artists`);
    if (!res.ok) throw new Error('Failed to fetch artists');
    return res.json();
  },

  getFeaturedArtists: async () => {
    const res = await fetch(`${API_BASE_URL}/artists/featured`);
    if (!res.ok) throw new Error('Failed to fetch featured artists');
    return res.json();
  },

  getArtistById: async (id: string) => {
    const res = await fetch(`${API_BASE_URL}/artists/${id}`);
    if (!res.ok) throw new Error('Failed to fetch artist');
    return res.json();
  },

  createArtist: async (formData: FormData) => {
    const res = await fetch(`${API_BASE_URL}/artists`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to create artist');
    }
    return res.json();
  },

  updateArtist: async (id: string, formData: FormData) => {
    const res = await fetch(`${API_BASE_URL}/artists/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to update artist');
    }
    return res.json();
  },

  deleteArtist: async (id: string) => {
    const res = await fetch(`${API_BASE_URL}/artists/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete artist');
    return res.json();
  },

  // --- PRODUCTS ---
  getProducts: async (): Promise<Product[]> => {
    const res = await fetch(`${API_BASE_URL}/products`);
    if (!res.ok) throw new Error('Failed to fetch products');
    return res.json();
  },

  getProduct: async (id: string): Promise<Product> => {
    const res = await fetch(`${API_BASE_URL}/products/${id}`);
    if (!res.ok) throw new Error('Failed to fetch product');
    return res.json();
  },

  getAdminProducts: async (): Promise<Product[]> => {
    const res = await fetch(`${API_BASE_URL}/products/admin`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch products');
    return res.json();
  },

  getPaymentMode: async (): Promise<PaymentMode> => {
    const res = await fetch(`${API_BASE_URL}/products/admin/payment-mode`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch payment mode');
    const data = await res.json();
    return data.mode;
  },

  // Two-step upload: ask the API for a presigned R2 target, then PUT the file
  // straight to R2. The file never passes through our serverless function, so
  // it isn't subject to the ~4.5MB request body cap.
  createProductUploadUrl: async (fileName: string, contentType: string): Promise<ProductUploadTarget> => {
    const res = await fetch(`${API_BASE_URL}/products/upload-url`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ fileName, contentType }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to prepare the upload');
    return data;
  },

  uploadFileToR2: (
    target: ProductUploadTarget,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<void> =>
    new Promise((resolve, reject) => {
      // XHR rather than fetch: it's the only way to get upload progress, which
      // matters because these files can be large.
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', target.url, true);
      // Must match the Content-Type the URL was signed with, or R2 rejects it.
      xhr.setRequestHeader('Content-Type', target.contentType);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        // R2 replies with an XML <Code> that says exactly what went wrong —
        // SignatureDoesNotMatch, AccessDenied, NoSuchBucket, and so on.
        const code = (xhr.responseText || '').match(/<Code>([^<]+)<\/Code>/)?.[1];
        const hint =
          xhr.status === 403
            ? ' The request reached R2 but was rejected — check that R2_ACCESS_KEY_ID / R2_SECRET_ACCESS_KEY are correct and that the API token has Object Read & Write on this bucket.'
            : xhr.status === 404
              ? ' The bucket was not found — check R2_BUCKET and R2_ACCOUNT_ID.'
              : '';
        reject(new Error(`Upload failed: HTTP ${xhr.status}${code ? ` (${code})` : ''}.${hint}`));
      };
      // A network-level failure. The browser deliberately hides CORS details
      // from JS, so the real reason is only visible in the devtools console —
      // but a blocked preflight is overwhelmingly the cause here.
      xhr.onerror = () =>
        reject(
          new Error(
            `Upload was blocked before reaching R2. This is almost always the bucket's CORS policy: it must list exactly "${window.location.origin}" under AllowedOrigins, with PUT in AllowedMethods and Content-Type in AllowedHeaders. Open the browser console for the exact error.`
          )
        );
      xhr.send(file);
    }),

  createProduct: async (formData: FormData): Promise<Product> => {
    const res = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to create product');
    }
    return res.json();
  },

  updateProduct: async (id: string, formData: FormData): Promise<Product> => {
    const res = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to update product');
    }
    return res.json();
  },

  deleteProduct: async (id: string) => {
    const res = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete product');
    return res.json();
  },

  // --- ORDERS / CHECKOUT ---
  createOrder: async (payload: CreateOrderPayload): Promise<CreateOrderResponse> => {
    const res = await fetch(`${API_BASE_URL}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to start checkout');
    }
    return res.json();
  },

  verifyOrder: async (orderId: string): Promise<VerifyOrderResponse> => {
    const res = await fetch(`${API_BASE_URL}/orders/${orderId}/verify`, {
      method: 'POST',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to verify payment');
    return data;
  },

  abandonOrder: async (orderId: string) => {
    try {
      await fetch(`${API_BASE_URL}/orders/${orderId}/abandon`, { method: 'POST' });
    } catch {
      // best-effort — nothing for the UI to react to either way
    }
  },

  getAdminOrders: async (filters: OrdersAdminFilters = {}): Promise<OrderRecord[]> => {
    const params = new URLSearchParams();
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortDir) params.set('sortDir', filters.sortDir);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.status) params.set('status', filters.status);

    const queryString = params.toString();
    const res = await fetch(`${API_BASE_URL}/orders/admin${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Failed to fetch orders');
    }
    return res.json();
  },

  resyncOrder: async (orderId: string): Promise<{ message: string; order: OrderRecord }> => {
    const res = await fetch(`${API_BASE_URL}/orders/${orderId}/resync`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to resync order');
    return data;
  },

  // --- MAINTENANCE ---
  fixPostExcerpts: async (): Promise<{ total: number; updated: number }> => {
    const res = await fetch(`${API_BASE_URL}/posts/maintenance/fix-excerpts`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Failed to run excerpt fix');
    return res.json();
  },
};
